import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { auth } from '../config/firebase';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  authReady: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  logout: () => Promise<void>;
  loginError: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Subscribe to Firebase auth state changes. Fires once on mount with the
  // restored session (or null) and then on every login/logout.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoginError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err: any) {
      // Firebase error codes: auth/invalid-credential, auth/user-not-found,
      // auth/wrong-password, auth/too-many-requests, ...
      const code = err?.code || '';
      let msg = 'Invalid email or password';
      if (code === 'auth/too-many-requests') {
        msg = 'Too many attempts — try again later';
      } else if (code === 'auth/network-request-failed') {
        msg = 'Network error — check your connection';
      } else if (code === 'auth/user-disabled') {
        msg = 'This account is disabled';
      }
      setLoginError(msg);
      return false;
    }
  };

  // Google sign-in: 2-step verification and suspicious-login checks come from the Google account
  // itself. Self sign-up is disabled in Firebase, so only accounts added in the console get in,
  // and the server additionally checks ADMIN_EMAILS.
  const loginWithGoogle = async (): Promise<boolean> => {
    setLoginError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      return true;
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return false;
      }
      let msg = 'Google prijava nije uspjela';
      if (code === 'auth/admin-restricted-operation') {
        msg = 'Ovaj Google nalog nema pristup';
      } else if (code === 'auth/popup-blocked') {
        msg = 'Browser je blokirao prozor za prijavu, dozvolite popup za ovu stranicu';
      } else if (code === 'auth/network-request-failed') {
        msg = 'Greška u mreži, provjerite konekciju';
      } else if (code === 'auth/user-disabled') {
        msg = 'Ovaj nalog je ugašen';
      }
      setLoginError(msg);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    await signOut(auth);
  };

  const value: AuthContextType = {
    isAuthenticated: !!user,
    user,
    authReady,
    login,
    loginWithGoogle,
    logout,
    loginError,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export default AuthContext;
